import React from "react";
import { Modal, Form, Col, Row, Button } from "react-bootstrap";
import ProSourceForm from "../../components/ProSourceForm";
import ImageUpload from "../../components/ImageUpload";
import { BrandLogo } from "../BrandSetup";
import {
  Label,
  RequiredAsterisk,
  FormItem,
  InfoTooltip,
  KTLink,
} from "../../components/Base";
import {
  RESERVATION_COLOR,
  PRIMARY_COLOR,
  DANGER_COLOR,
} from "../../constants";
import core from "../../vendors/core-api/";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { fetchData, formatConsecutiveDates } from "../../helpers";
import { isEmpty } from "lodash";
import { format } from "date-fns";
import moment from "moment";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

const MySwal = withReactContent(Swal);

export default class EditTableModal extends ProSourceForm {
  constructor(props) {
    super(props);
    const { selected_table, isEdit } = props;
    const initialDiningServices =
      isEdit && selected_table && selected_table.dining_services
        ? selected_table.dining_services
        : [];
    const initialErrors = {};
    if (isEmpty(initialDiningServices)) {
      initialErrors.dining_services =
        "At least one dining service is required for this table.";
    }

    this.state = {
      values: {
        name: isEdit ? selected_table.name : "",
        reservation_fee:
          isEdit && selected_table.item ? selected_table.item.price : 0,
        minimum: isEdit ? selected_table.minimum : 1,
        maximum: isEdit ? selected_table.maximum : 0,
        limit: isEdit ? selected_table.limit : 0,
        isReservable: isEdit ? selected_table.isReservable : false,
        isExclusive: isEdit ? selected_table.isExclusive : false,
        is_communal: isEdit ? selected_table.isCommunal : false,
        image_url:
          isEdit && !isEmpty(selected_table.item)
            ? selected_table.item.image_url
            : "",
        description:
          isEdit && !isEmpty(selected_table.item)
            ? selected_table.item.description
            : "",
        dining_services: initialDiningServices,
        blockout_dates:
          isEdit && selected_table.blockout_dates
            ? selected_table.blockout_dates
            : [],
        max_reservable_days:
          isEdit && selected_table.max_reservable_days
            ? selected_table.max_reservable_days
            : 20,
        seating_codes:
          isEdit && selected_table.seating_codes
            ? selected_table.seating_codes
            : [],
      },
      errors: initialErrors,
      touched: isEmpty(initialDiningServices) ? { dining_services: true } : {},
      isSubmitting: false,
      isEdit: props.isEdit || false,
      filePath: "",
      file: null,
      blockoutDatesModal: false,
      showAllBlockoutDates: false,
      seatingCodeInput: "",
      seatingCodeError: "",
    };
    this.fileapi = core("puf").get();
    this.calendarRef = React.createRef();
    this.validate = {
      required: ["name", "minimum", "maximum", "image_url", "description"],
    };
    this.dateClick = this.dateClick.bind(this);
    this.dayCellClassNames = this.dayCellClassNames.bind(this);
    this.handleEventClick = this.handleEventClick.bind(this);
  }

  handleFormValidation = () => {
    let { errors = {}, values = {} } = this.state;
    const nextErrors = { ...errors };

    // String fields: name, description, image_url
    // Validate presence (checks null/undefined/whitespace-only, preserves '0')
    const stringRequired = ["name", "description", "image_url"];
    for (const name of stringRequired) {
      const value = values[name];
      if (value == null || !String(value).trim()) {
        nextErrors[name] = "This field is required";
      } else if (nextErrors[name] === "This field is required") {
        delete nextErrors[name];
      }
    }

    // Numeric pax fields: minimum, maximum
    const numericPax = ["minimum", "maximum"];
    for (const name of numericPax) {
      const value = values[name];
      if (value == null || value === "" || isNaN(Number(value))) {
        nextErrors[name] = "This field is required";
      } else if (Number(value) < 1) {
        nextErrors[name] = "Minimum value should be 1";
      } else if (
        nextErrors[name] === "This field is required" ||
        nextErrors[name] === "Minimum value should be 1"
      ) {
        delete nextErrors[name];
      }
    }

    // Pax range validation: minimum <= maximum
    if (
      values.minimum != null &&
      values.maximum != null &&
      values.minimum !== "" &&
      values.maximum !== "" &&
      Number(values.minimum) >= 1 &&
      Number(values.maximum) >= 1
    ) {
      if (Number(values.minimum) > Number(values.maximum)) {
        nextErrors.maximum = "Value is invalid";
      } else if (nextErrors.maximum === "Value is invalid") {
        delete nextErrors.maximum;
      }
    }

    // Dining services requirement
    if (isEmpty(values.dining_services)) {
      nextErrors.dining_services =
        "At least one dining service is required for this table.";
    } else {
      delete nextErrors.dining_services;
    }

    return nextErrors;
  };

  handleFeedbackError = (field_name = "", type = "helper-text") => {
    const { errors = {} } = this.state;
    if (!this.isTouched(field_name)) {
      return type === "helper-text" ? <></> : type === "input" ? "" : undefined;
    }
    if (type === "helper-text") {
      return errors.hasOwnProperty(field_name) ? (
        <div className="invalid-feedback">{errors[field_name]}</div>
      ) : (
        <></>
      );
    } else if (type === "input") {
      return errors.hasOwnProperty(field_name) ? "is-invalid" : "";
    } else if (type === "error-text") {
      return errors[field_name];
    }
  };

  handleOnSubmit = async (event) => {
    event.preventDefault();

    const keys = this.getFields();
    const touched = { ...this.state.touched };
    for (const key of keys) {
      touched[key] = true;
    }
    touched.dining_services = true;

    const errors = this.handleFormValidation();
    this.setState({ touched, errors });

    if (Object.keys(errors).length === 0) {
      this.setSubmitting();
      this.onSubmit && this.onSubmit(this.state.values, this.setSubmitting);
    }
  };

  onToggleDiningService = (service, isChecked) => {
    const { values = {}, errors = {}, touched = {} } = this.state;
    let currentServices = [...(values.dining_services || [])];

    if (isChecked) {
      currentServices = currentServices.filter(
        (serv) => serv._id !== service._id,
      );
    } else {
      currentServices.push(service);
    }

    const nextTouched = { ...touched, dining_services: true };
    const nextErrors = { ...errors };

    if (isEmpty(currentServices)) {
      nextErrors.dining_services =
        "At least one dining service is required for this table.";
    } else {
      delete nextErrors.dining_services;
    }

    this.setState({
      values: {
        ...values,
        dining_services: currentServices,
      },
      errors: nextErrors,
      touched: nextTouched,
    });
  };

  // Seating code token format: one or more letters followed by one or more digits (e.g. C1, PDR12)
  _isValidToken = (token) => /^[A-Za-z]+\d+$/.test(token.trim());

  // Expand a dash-range like C1-C7 into [C1, C2, C3, C4, C5, C6, C7]
  _expandRange = (start, end) => {
    const startMatch = start.match(/^([a-zA-Z]+)(\d+)$/);
    const endMatch = end.match(/^([a-zA-Z]+)(\d+)$/);

    if (!startMatch || !endMatch) return null;

    const startPrefix = startMatch[1].toUpperCase();
    const endPrefix = endMatch[1].toUpperCase();
    const from = parseInt(startMatch[2], 10);
    const to = parseInt(endMatch[2], 10);

    if (startPrefix !== endPrefix || from > to) {
      return null;
    }

    const result = [];
    for (let i = from; i <= to; i++) result.push(`${startPrefix}${i}`);
    return result;
  };

  onSeatingCodeInputChange = (e) => {
    this.setState({ seatingCodeInput: e.target.value, seatingCodeError: "" });
  };

  onAddSeatingCodes = () => {
    const { seatingCodeInput } = this.state;
    const raw = seatingCodeInput.trim();
    if (!raw) return;

    // Split by commas first
    const segments = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const resolved = [];
    let error = "";

    for (const seg of segments) {
      if (seg.includes("-")) {
        // Could be a range like C1-C7
        const parts = seg.split("-");
        if (parts.length !== 2) {
          error = `Invalid range: "${seg}". Use format like C1-C7.`;
          break;
        }
        const [start, end] = parts.map((p) => p.trim());
        if (!this._isValidToken(start) || !this._isValidToken(end)) {
          error = `Invalid token in range "${seg}". Each code must start with a letter then digits (e.g. C1).`;
          break;
        }
        const expanded = this._expandRange(start, end);
        if (!expanded) {
          error = `Invalid range "${seg}". Ensure same letter prefix and start ≤ end.`;
          break;
        }
        resolved.push(...expanded);
      } else {
        if (!this._isValidToken(seg)) {
          error = `Invalid code "${seg}". Each code must start with a letter then digits (e.g. C1).`;
          break;
        }
        resolved.push(seg.trim().toUpperCase());
      }
    }

    if (error) {
      this.setState({ seatingCodeError: error });
      return;
    }

    // Merge with existing codes (avoid duplicates)
    const existing = this.state.values.seating_codes || [];
    const merged = Array.from(
      new Set([...existing, ...resolved.map((c) => c.toUpperCase())]),
    );
    this.setFieldValue("seating_codes", merged);
    this.setState({ seatingCodeInput: "", seatingCodeError: "" });
  };

  onRemoveSeatingCode = (code) => {
    const updated = (this.state.values.seating_codes || []).filter(
      (c) => c !== code,
    );
    this.setFieldValue("seating_codes", updated);
  };

  changeModalVisible = (code, visible) => {
    this.setState({ [code]: visible }, () => {
      if (code === "blockoutDatesModal" && visible) {
        setTimeout(() => {
          this.calendarRef.current?.getApi().updateSize();
        }, 100);
      }
    });
  };

  dateClick = (info) => {
    // The handleEventClick method already handles toggling (adding/removing) the date.
    // The unselect() method is for clearing a *selection range*, not for individual date styling/events.
    // So, we just call handleEventClick to toggle the date's blocked status.
    this.handleEventClick(info.dateStr);
  };

  dayCellClassNames = (arg) => {
    const dateStr = format(arg.date, "yyyy-MM-dd");
    if (this.state.values.blockout_dates.includes(dateStr)) {
      return ["events-disabled-day"];
    }
    return [];
  };

  handleEventClick = (clicked_date) => {
    const { values } = this.state;
    const blockoutDates = [...values.blockout_dates];
    const dateIndex = blockoutDates.indexOf(clicked_date);
    if (dateIndex > -1) blockoutDates.splice(dateIndex, 1);
    else blockoutDates.push(clicked_date);
    this.setFieldValue("blockout_dates", blockoutDates);
  };

  onChange = (e) => {
    let name = e.target.name,
      value = e.currentTarget.value;
    let { values = {}, errors = {} } = this.state;
    const nextErrors = { ...errors };

    if (name === "name") {
      if (isEmpty(value.trim())) {
        nextErrors.name = "This field is required";
      } else {
        delete nextErrors.name;
      }
    }

    if (name === "minimum" || name === "maximum") {
      if (value === "" || value == null) {
        nextErrors[name] = "This field is required";
      } else if (value === "0" || Number(value) < 1) {
        nextErrors[name] = "Minimum value should be 1";
      } else {
        if (
          nextErrors[name] === "This field is required" ||
          nextErrors[name] === "Minimum value should be 1"
        ) {
          delete nextErrors[name];
        }
      }

      if (
        name === "maximum" &&
        values["minimum"] > 0 &&
        values["maximum"] > 0
      ) {
        delete nextErrors["minimum"];
      }

      if (
        Number(values["minimum"]) >= 1 &&
        Number(values["maximum"]) >= 1 &&
        Number(values["minimum"]) > Number(values["maximum"])
      ) {
        nextErrors.maximum = "Value is invalid";
      } else if (nextErrors.maximum === "Value is invalid") {
        delete nextErrors.maximum;
      }
    }

    if (name === "reservation_fee") {
      if (value >= 0 && value < 100) {
        nextErrors.reservation_fee = "Minimum required fee is 100";
      } else {
        delete nextErrors.reservation_fee;
      }
    }

    this.setState({ values, errors: nextErrors });
  };

  onUpdateTable = async (values, setSubmitting) => {
    try {
      const { selected_table = {}, store = {} } = this.props;
      setSubmitting(true);
      const { data } = await fetchData(
        "put",
        `/store/${store._id}/update-table/${selected_table._id}`,
        { ...values },
      );
      console.log(values, "values");
      if (data.success) {
        this.setState({ errors: {}, touched: {} });
        this.props.hideEditModal();
        this.props.getData();
        MySwal.fire({
          icon: "success",
          showConfirmButton: false,
          title: "Success!",
          toast: true,
          timer: 1500,
          text: "You have successfully saved your changes.",
        });
      }
    } catch (err) {
      setSubmitting(false);
      if (err.hasOwnProperty("response")) {
        const { data = {} } = err.response,
          { error_message = {} } = data;
        if (!isEmpty(error_message)) {
          MySwal.fire({
            icon: "error",
            title: "Ooops!",
            text: error_message,
            confirmButtonColor: PRIMARY_COLOR,
            confirmButtonText: "OK",
            showCancelButton: false,
          });
        } else {
          MySwal.fire({
            icon: "error",
            title: "Ooops!",
            text: "Something went wrong. Please try again.",
            confirmButtonColor: DANGER_COLOR,
            confirmButtonText: "Ok",
          });
        }
      }
    }
  };

  onCreateTable = async (values, setSubmitting) => {
    try {
      const { store = {} } = this.props;
      setSubmitting(true);
      const { data } = await fetchData(
        "post",
        `/store/${store._id}/add-reservable-table`,
        { ...values },
      );
      if (data.success) {
        this.setState({ errors: {}, touched: {} });
        this.props.hideEditModal();
        this.props.getData();
        MySwal.fire({
          icon: "success",
          showConfirmButton: false,
          toast: true,
          title: "Success!",
          text: "You have successfully saved your changes.",
          confirmButtonColor: PRIMARY_COLOR,
          timer: 1000,
        });
      }
    } catch (err) {
      setSubmitting(false);
      if (err.hasOwnProperty("response")) {
        const { data = {} } = err.response,
          { error_message = {} } = data;
        if (!isEmpty(error_message)) {
          MySwal.fire({
            icon: "error",
            title: "Ooops!",
            text: error_message,
            confirmButtonColor: PRIMARY_COLOR,
            confirmButtonText: "OK",
            showCancelButton: false,
          });
        } else {
          MySwal.fire({
            icon: "error",
            title: "Ooops!",
            text: "Something went wrong. Please try again.",
            confirmButtonColor: DANGER_COLOR,
            confirmButtonText: "Ok",
          });
        }
      }
    }
  };

  handlePhotoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await this.fileapi.post({
        url: "/util/upload-to-aws/items",
        data: formData,
      });
      return data.filePath;
    } catch (error) {
      throw error;
    }
  };

  onSubmit = async (values, setSubmitting) => {
    if (isEmpty(values.dining_services)) {
      this.setState({
        errors: {
          ...this.state.errors,
          dining_services:
            "At least one dining service is required for this table.",
        },
        touched: {
          ...this.state.touched,
          dining_services: true,
        },
      });
      MySwal.fire({
        icon: "error",
        title: "Dining Service Required",
        text: "Please select at least one dining service for this table.",
        confirmButtonColor: PRIMARY_COLOR,
        confirmButtonText: "OK",
      });
      setSubmitting(false);
      return;
    }

    if (this.state.file) {
      try {
        setSubmitting(true);
        const file_path = await this.handlePhotoUpload(this.state.file);
        if (!isEmpty(file_path)) {
          values.image_url = file_path;
        }
      } catch (uploadError) {
        setSubmitting(false);
        MySwal.fire({
          icon: "error",
          title: "Upload Failed",
          text: "Failed to upload image. Please try again.",
          confirmButtonColor: PRIMARY_COLOR,
          confirmButtonText: "OK",
        });
        return;
      }
    }
    if (this.props.isEdit) {
      this.onUpdateTable(values, setSubmitting);
    } else {
      this.onCreateTable(values, setSubmitting);
    }
  };

  onFileError = (error, maxFileSizeText = "200KB") => {
    if (error === "size-too-much") {
      MySwal.fire({
        icon: "error",
        title: "Ooops!",
        text: `File is too big! Maximum upload size is ${maxFileSizeText}.`,
        confirmButtonColor: PRIMARY_COLOR,
        confirmButtonText: "OK",
        showCancelButton: false,
      });
    } else if (error === "unsupported-file-extension") {
      MySwal.fire({
        icon: "error",
        title: "Ooops!",
        text: `File type unsupported. Please upload PNG, JPG and JPEG files only.`,
        confirmButtonColor: PRIMARY_COLOR,
        confirmButtonText: "OK",
        showCancelButton: false,
      });
    } else {
      MySwal.fire({
        icon: "error",
        title: "Ooops!",
        text: `Something went wrong`,
        confirmButtonColor: PRIMARY_COLOR,
        confirmButtonText: "OK",
        showCancelButton: false,
      });
    }
  };

  onToggleCommunal = async (e) => {
    const { checked } = e.target;
    this.setFieldValue("is_communal", checked);
  };
  render() {
    const { isSubmitting, errors, values, isEdit, showAllBlockoutDates } =
      this.state;
    const { show, hideEditModal, onDeleteTable, selected_table } = this.props;

    const actions = {
        handleFeedbackError: this.handleFeedbackError,
        isTouched: this.isTouched,
      },
      inputActions = {
        onChange: this.handleOnChange,
        onBlur: this.handleOnBlur,
      };

    return (
      <>
        <Modal show={show} onHide={hideEditModal} centered size="lg">
          <style>{`
          .events-disabled-day { background-color: #e8dffa; }
        `}</style>
          <Modal.Header closeButton>
            <Modal.Title>{`${isEdit ? "Edit" : "Add"} Table`}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={this.handleOnSubmit}>
            <Modal.Body>
              <div className="d-flex flex-row">
                <div
                  className="d-flex flex-column px-8 text-center"
                  style={{ flexBasis: "30%" }}
                >
                  {values.image_url && (
                    <BrandLogo
                      style={{ width: "150px" }}
                      alt={"Table Preview"}
                      src={values.image_url}
                      onClickRemove={() => {
                        const { values = {}, touched = {} } = this.state;
                        this.setState(
                          {
                            file: null,
                            values: { ...values, image_url: "" },
                            touched: { ...touched, image_url: true },
                          },
                          () => this.validateForm(),
                        );
                      }}
                    />
                  )}
                  {isEmpty(values.image_url) && (
                    <ImageUpload
                      onError={this.onFileError}
                      onChange={(file, preview) => {
                        const { values = {}, touched = {} } = this.state;
                        this.setState(
                          {
                            file,
                            values: { ...values, image_url: preview },
                            touched: { ...touched, image_url: true },
                          },
                          () => this.validateForm(),
                        );
                      }}
                      label={
                        <div style={{ margin: "1rem 0", textAlign: "center" }}>
                          <Label
                            text={
                              <>
                                <span>
                                  {" "}
                                  Table Preview Photo <RequiredAsterisk />
                                </span>
                              </>
                            }
                          />
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.8rem" }}
                          >
                            Maximum file size is 2MB. <br />
                          </div>
                          <div
                            className="text-danger"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {this.handleFeedbackError(
                              "image_url",
                              "error-text",
                            )}
                          </div>
                        </div>
                      }
                      maxFileSize={2000}
                      maxFileSizeText={"2MB"}
                    />
                  )}
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ flexBasis: "68%" }}
                >
                  <Col>
                    <FormItem
                      label="Table Name"
                      name={"name"}
                      event
                      inputClassName="text-capitalize"
                      inputProps={{
                        name: "name",
                        placeholder: values["name"],
                        ...(this.isTouched("name") && {
                          isValid: isEmpty(errors.name),
                          isInvalid: !isEmpty(errors.name),
                        }),
                        value: values["name"],
                        style: {
                          borderColor: RESERVATION_COLOR,
                        },
                      }}
                      inputActions={inputActions}
                      actions={actions}
                      normal
                      showRequired
                    />
                    <Row className="d-flex px-4 justify-content-space-between">
                      <FormItem
                        type="number"
                        event
                        style={{
                          flexBasis: values.is_communal ? "32%" : "48%",
                        }}
                        label={
                          <>
                            Minimum Pax
                            {InfoTooltip("Minimum value is 1")}
                          </>
                        }
                        name={"minimum"}
                        inputClassName="text-capitalize"
                        inputProps={{
                          min: 1,
                          name: "minimum",
                          placeholder: values["minimum"],
                          ...(this.isTouched("minimum") && {
                            isValid: isEmpty(errors.minimum),
                            isInvalid: !isEmpty(errors.minimum),
                          }),
                          value: values["minimum"],
                          style: {
                            borderColor: RESERVATION_COLOR,
                          },
                        }}
                        inputActions={inputActions}
                        actions={actions}
                        normal
                        showRequired
                      />
                      <FormItem
                        style={{
                          flexBasis: values.is_communal ? "32%" : "48%",
                        }}
                        event
                        type="number"
                        label={
                          <>
                            Maximum Pax
                            {InfoTooltip("Minimum value is 1")}
                          </>
                        }
                        name={"maximum"}
                        inputClassName="text-capitalize"
                        inputProps={{
                          name: "maximum",
                          placeholder: values["maximum"],
                          ...(this.isTouched("maximum") && {
                            isValid: isEmpty(errors.maximum),
                            isInvalid: !isEmpty(errors.maximum),
                          }),
                          value: values["maximum"],
                          style: {
                            borderColor: RESERVATION_COLOR,
                          },
                          min: 1,
                        }}
                        inputActions={inputActions}
                        actions={actions}
                        normal
                        showRequired
                      />
                      {values.is_communal && (
                        <FormItem
                          style={{
                            flexBasis: values.is_communal ? "32%" : "48%",
                          }}
                          event
                          type="number"
                          label={
                            <>
                              Capacity
                              {InfoTooltip(
                                "Set the total capacity for this communal table.",
                              )}
                            </>
                          }
                          name={"limit"}
                          inputClassName="text-capitalize"
                          inputProps={{
                            name: "limit",
                            placeholder: "0",
                            value: values["limit"],
                            style: {
                              borderColor: RESERVATION_COLOR,
                            },
                            min: 0,
                          }}
                          inputActions={inputActions}
                          actions={actions}
                          normal
                        />
                      )}
                    </Row>
                    <Row className="d-flex px-4 justify-content-space-between">
                      {isEdit && (
                        <div
                          style={{
                            flexBasis: "50%",
                          }}
                          className="d-flex align-items-center"
                        >
                          <label className="text-dark font-weight-bolder mr-4 ">
                            Reservable:
                          </label>
                          <span className="switch switch-outline switch-icon switch-info">
                            <label className="mb-0">
                              <input
                                style={{ height: 28 }}
                                type="checkbox"
                                name="isReservable"
                                defaultChecked={values.isReservable}
                                {...inputActions}
                                onChange={(e) => {
                                  if (e.target) {
                                    const { values } = this.state;
                                    this.setState({
                                      values: {
                                        ...values,
                                        isReservable: e.target.checked,
                                      },
                                    });
                                  }
                                }}
                              />
                              <span></span>
                            </label>
                          </span>
                        </div>
                      )}
                      <div
                        style={{
                          flexBasis: "50%",
                        }}
                        className="d-flex align-items-center"
                      >
                        <label className="text-dark font-weight-bolder mr-4 ">
                          Exclusive
                          {InfoTooltip("This will be an exclusive table.")}
                        </label>
                        <span className="switch switch-outline switch-icon switch-info">
                          <label className="mb-0">
                            <input
                              style={{ height: 28 }}
                              type="checkbox"
                              name="isExclusive"
                              checked={values.isExclusive}
                              {...inputActions}
                              onChange={(e) =>
                                this.setFieldValue(
                                  "isExclusive",
                                  e.target.checked,
                                )
                              }
                            />
                            <span></span>
                          </label>
                        </span>
                      </div>
                      <div
                        style={{
                          flexBasis: "50%",
                        }}
                        className="d-flex align-items-center"
                      >
                        <label className="text-dark font-weight-bolder mr-4 ">
                          Communal Table{" "}
                          {InfoTooltip(
                            "This is will allow communal dining on this table ",
                          )}
                        </label>
                        <span className="switch switch-outline switch-icon switch-info">
                          <label className="mb-0">
                            <input
                              style={{ height: 28 }}
                              type="checkbox" // Changed
                              name="is_communal" // Changed
                              checked={values.is_communal} // Changed
                              {...inputActions}
                              onChange={this.onToggleCommunal} // Changed
                            />
                            <span></span>
                          </label>
                        </span>
                      </div>
                    </Row>
                    {!isEmpty(this.props.diningServices) ? (
                      <div className="d-flex w-100 flex-column mt-4">
                        <p className="font-bolder mb-1">
                          Set Dining Services for this Table{" "}
                          <RequiredAsterisk />
                        </p>
                        <span className="text-muted font-size-sm mb-2">
                          Select at least one dining service for this table.
                        </span>

                        {isEmpty(values.dining_services) && (
                          <div
                            className="alert alert-custom alert-light-danger py-2 px-4 mb-2 font-size-sm font-weight-bold d-flex align-items-center"
                            style={{ borderRadius: "8px" }}
                          >
                            <i className="fas fa-exclamation-circle text-danger mr-2"></i>
                            <span>
                              {errors.dining_services ||
                                "At least one dining service is required for this table."}
                            </span>
                          </div>
                        )}

                        {this.props.diningServices.map(
                          (service, service_idx) => {
                            const isChecked = values.dining_services.some(
                              (serv) => serv._id === service._id,
                            );
                            return (
                              <div
                                key={`dining-service-${service_idx}`}
                                className="w-100 py-1"
                              >
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  style={{
                                    borderColor: isChecked
                                      ? RESERVATION_COLOR
                                      : "#B5B5C3",
                                    color: isChecked
                                      ? RESERVATION_COLOR
                                      : "#B5B5C3",
                                    borderRadius: 25,
                                    backgroundColor: "#ffff",
                                  }}
                                  className="btn btn-outline-pickup w-100 text-left d-flex flex-row"
                                >
                                  {" "}
                                  <label
                                    className={`top-0 checkbox checkbox-outline checkbox-circle checkbox-${
                                      isChecked ? "info" : "light"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() =>
                                        this.onToggleDiningService(
                                          service,
                                          isChecked,
                                        )
                                      }
                                    />
                                    <span />
                                  </label>
                                  {service.name}
                                </Button>
                              </div>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <div className="d-flex w-100 flex-column mt-4">
                        <p className="font-bolder mb-1">
                          Set Dining Services for this Table{" "}
                          <RequiredAsterisk />
                        </p>
                        <div
                          className="alert alert-custom alert-light-danger py-2 px-4 mb-2 font-size-sm font-weight-bold d-flex align-items-center"
                          style={{ borderRadius: "8px" }}
                        >
                          <i className="fas fa-exclamation-triangle text-danger mr-2"></i>
                          <span>
                            No dining services found for this store. Please
                            create at least one dining service first before
                            saving this table.
                          </span>
                        </div>
                      </div>
                    )}{" "}
                    <div className="d-flex align-items-center mt-4">
                      {/* Label + Tooltip */}
                      <label className="font-weight-bold mb-0 mr-3 d-flex align-items-center">
                        <span className="mr-2">
                          Set Max Advance Booking (Days)
                        </span>
                        {InfoTooltip(
                          "Set how many days in advance a customer can book this table.",
                        )}
                      </label>

                      {/* Input Field */}
                      <div style={{ width: "120px" }}>
                        <FormItem
                          type="number"
                          event
                          label=""
                          name="max_reservable_days"
                          inputClassName="text-capitalize"
                          inputProps={{
                            name: "max_reservable_days",
                            placeholder: "20",
                            value: values["max_reservable_days"],
                            min: 20,
                          }}
                          inputActions={inputActions}
                          actions={actions}
                          normal
                        />
                      </div>
                    </div>
                    {/* Seating Codes */}
                    <div className="d-flex w-100 flex-column mt-4">
                      <label className="font-weight-bold mb-1 d-flex align-items-center">
                        <span className="mr-2">Seating Codes</span>
                        {InfoTooltip(
                          "Assign seat codes to this table. Use ranges (e.g. C1-C7) or individual codes separated by commas (e.g. B1,B7).",
                        )}
                      </label>
                      <div className="d-flex align-items-start">
                        <div className="flex-grow-1 mr-2">
                          <Form.Control
                            type="text"
                            placeholder="e.g. C1-C7, B1, B7"
                            value={this.state.seatingCodeInput}
                            onChange={this.onSeatingCodeInputChange}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                this.onAddSeatingCodes();
                              }
                            }}
                            style={{ borderColor: RESERVATION_COLOR }}
                            isInvalid={!!this.state.seatingCodeError}
                          />
                          {this.state.seatingCodeError && (
                            <Form.Text className="text-danger">
                              {this.state.seatingCodeError}
                            </Form.Text>
                          )}
                          <Form.Text className="text-muted">
                            Use <strong>C1-C7</strong> for ranges or{" "}
                            <strong>B1,B7</strong> for individual codes. Press
                            Enter or click Add.
                          </Form.Text>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm font-weight-bold"
                          style={{
                            backgroundColor: RESERVATION_COLOR,
                            color: "#fff",
                            borderRadius: "6px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={this.onAddSeatingCodes}
                        >
                          + Add
                        </button>
                      </div>

                      {/* Display added seating codes */}
                      {values.seating_codes &&
                      values.seating_codes.length > 0 ? (
                        <div
                          className="mt-2 d-flex flex-wrap"
                          style={{ gap: "6px" }}
                        >
                          {values.seating_codes.map((code) => (
                            <span
                              key={code}
                              className="d-inline-flex align-items-center"
                              style={{
                                backgroundColor: "#f0eaff",
                                border: `1px solid ${RESERVATION_COLOR}`,
                                color: RESERVATION_COLOR,
                                borderRadius: "20px",
                                padding: "2px 10px",
                                fontSize: "0.82rem",
                                fontWeight: 600,
                              }}
                            >
                              {code}
                              <button
                                type="button"
                                onClick={() => this.onRemoveSeatingCode(code)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: RESERVATION_COLOR,
                                  padding: "0 0 0 6px",
                                  lineHeight: 1,
                                  fontSize: "0.9rem",
                                }}
                                aria-label={`Remove ${code}`}
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p
                          className="text-muted mt-2 mb-0"
                          style={{ fontSize: "0.85rem" }}
                        >
                          No seating codes added yet.
                        </p>
                      )}
                    </div>
                    <div className="d-flex w-100 flex-column mt-4">
                      <p className="font-bolder mb-2">
                        Set Blockout Dates for this Table
                      </p>
                      {values.blockout_dates.length > 0 ? (
                        <div className="mb-3">
                          <p className="mb-0">
                            <i
                              className="far fa-calendar-alt mr-2"
                              style={{ color: RESERVATION_COLOR }}
                            />
                            {formatConsecutiveDates(
                              values.blockout_dates.filter(
                                (d) => !moment(d).isBefore(moment(), "day"),
                              ),
                            )}
                          </p>
                        </div>
                      ) : (
                        <p className="text-muted">No blockout dates set.</p>
                      )}
                      <KTLink
                        style={{
                          color: RESERVATION_COLOR,
                          textAlign: "end",
                          textDecoration: "underline",
                        }}
                        text={
                          <>
                            {values.blockout_dates.length > 0
                              ? "Edit Blockout Dates"
                              : "+ Add Blockout Dates"}
                          </>
                        }
                        onClick={() =>
                          this.changeModalVisible("blockoutDatesModal", true)
                        }
                      />
                    </div>
                  </Col>
                </div>
              </div>

              <div className="d-flex flex-row w-100 px-8 pt-0">
                <FormItem
                  customFormControl
                  event
                  label={"Description"}
                  name={"description"}
                  actions={actions}
                  style={{
                    flexBasis: "100%",
                  }}
                  custom={
                    <>
                      <Form.Control
                        as={"textarea"}
                        style={{
                          height: 200,
                          borderColor: RESERVATION_COLOR,
                        }}
                        maxLength={500}
                        name="description"
                        placeholder="Description"
                        value={values["description"]}
                        {...inputActions}
                      />
                      <Form.Text className="text-right">
                        {values["description"].length} / 500
                      </Form.Text>
                    </>
                  }
                  showRequired
                  normal
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex flex-row justify-content-space-between w-100">
                <div className="d-flex flex-row ">
                  {isEdit && (
                    <button
                      type="button"
                      disabled={isSubmitting || isEmpty(selected_table.item)}
                      onClick={() => {
                        MySwal.fire({
                          icon: "warning",
                          title: "Are you sure?",
                          text: `You are about to delete this store table.`,
                          confirmButtonColor: PRIMARY_COLOR,
                          confirmButtonText: "Yes",
                          showCancelButton: true,
                          cancelButtonText: "No",
                        }).then(async (result) => {
                          if (result.value) {
                            this.setState({ isSubmitting: true });
                            await onDeleteTable(selected_table._id);
                          }
                        });
                      }}
                      className="btn btn-link px-9 py-3"
                      style={{ color: RESERVATION_COLOR }}
                    >
                      Delete Table
                    </button>
                  )}
                </div>
                <div className="d-flex flex-row ">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={hideEditModal}
                    className="btn btn-danger px-9 py-3 mr-3"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={"btn btn-info font-weight-bold px-9 py-2"}
                    disabled={isSubmitting || !isEmpty(errors)}
                  >
                    <div className="d-flex align-items-center py-1">
                      {isSubmitting ? (
                        <>
                          Saving...
                          <div
                            className="spinner spinner-sm text-info ml-2"
                            role="status"
                            aria-hidden="true"
                          ></div>
                        </>
                      ) : (
                        <span>Save</span>
                      )}
                    </div>
                  </button>
                </div>
              </div>
            </Modal.Footer>
          </Form>
        </Modal>
        <Modal
          size="xl"
          centered
          show={this.state.blockoutDatesModal}
          onHide={() => this.changeModalVisible("blockoutDatesModal", false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Blockout Dates</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="events-calendar-wrapper">
              <FullCalendar
                className="events-calendar-content" // Changed height to auto for responsiveness
                height="auto"
                headerToolbar={{
                  start: "",
                  center: "title",
                  end: "prev,next",
                }}
                validRange={{
                  start: new Date(),
                }}
                showNonCurrentDates={false}
                fixedWeekCount={false}
                selectable={true}
                dateClick={this.dateClick}
                dayCellClassNames={this.dayCellClassNames}
                events={values.blockout_dates.map((date) => ({
                  start: date,
                  display: "background",
                  backgroundColor: "#8950fc",
                }))}
                ref={this.calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                weekends={true}
              />
              <div className="d-flex justify-content-between align-items-start mt-4">
                <div>
                  <div className="d-flex align-items-center">
                    <div
                      className="mr-2"
                      style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#e8dffa",
                        border: "1px solid #e8dffa",
                        borderRadius: "4px",
                      }}
                    ></div>
                    <span>Blocked-out Date</span>
                  </div>
                </div>
                <div className="text-right" style={{ maxWidth: "45%" }}>
                  <p className="mb-2">
                    Click on a date to block or unblock it for this table.
                  </p>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className={"btn btn-info font-weight-bold"}
              onClick={() =>
                this.changeModalVisible("blockoutDatesModal", false)
              }
            >
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}
